import React from "react";
import { motion } from "framer-motion";
import { Leaf, Building2, Droplets, RefreshCw, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

// ── Animation helpers ─────────────────────────────────────────────
const fadeUp = (delay = 0) => ({
  initial:     { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0  },
  viewport:    { once: true, amount: 0.15 },
  transition:  { duration: 0.55, ease: "easeOut", delay },
});

const fadeLeft = (delay = 0) => ({
  initial:     { opacity: 0, x: -28 },
  whileInView: { opacity: 1, x: 0   },
  viewport:    { once: true, amount: 0.15 },
  transition:  { duration: 0.55, ease: "easeOut", delay },
});

const fadeRight = (delay = 0) => ({
  initial:     { opacity: 0, x: 28 },
  whileInView: { opacity: 1, x: 0  },
  viewport:    { once: true, amount: 0.15 },
  transition:  { duration: 0.55, ease: "easeOut", delay },
});

// ── SDG Data ──────────────────────────────────────────────────────
const SDGS = [
  {
    number:    "6",
    color:     "#26BDE2",
    lightBg:   "bg-[#26BDE2]/8",
    border:    "border-[#26BDE2]/20",
    iconColor: "text-[#26BDE2]",
    numColor:  "text-[#26BDE2]",
    icon:      Droplets,
    title:     "Clean Water & Sanitation",
    tagline:   "SDG 6",
    desc:      "SMPACE-2026 promotes research in water resources planning, groundwater modelling, recharge techniques, and advanced water & wastewater treatment systems to ensure safe and clean water for all.",
    topics: [
      "Water Resources Planning & Management",
      "Ground Water Modeling & Recharge",
      "Water and Wastewater Treatment",
      "Municipal Solid Waste Management",
    ],
  },
  {
    number:    "9",
    color:     "#F36D25",
    lightBg:   "bg-[#F36D25]/8",
    border:    "border-[#F36D25]/20",
    iconColor: "text-[#F36D25]",
    numColor:  "text-[#F36D25]",
    icon:      Building2,
    title:     "Industry, Innovation & Infrastructure",
    tagline:   "SDG 9",
    desc:      "The conference drives innovation in civil engineering infrastructure through advanced materials, structural health monitoring, ground improvement techniques, and smart construction management practices.",
    topics: [
      "Structural Health Monitoring",
      "Ground Improvement Techniques",
      "Advances in Cement, Admixtures & Concrete",
      "Time & Cost Optimization in Projects",
    ],
  },
  {
    number:    "11",
    color:     "#F99D26",
    lightBg:   "bg-[#F99D26]/8",
    border:    "border-[#F99D26]/20",
    iconColor: "text-[#F99D26]",
    numColor:  "text-[#F99D26]",
    icon:      Building2,
    title:     "Sustainable Cities & Communities",
    tagline:   "SDG 11",
    desc:      "Research presented at SMPACE-2026 contributes to sustainable urban infrastructure through eco-friendly construction, transportation planning, remote sensing applications, and resilient built environments.",
    topics: [
      "Pavement Materials Evaluation",
      "Application of Remote Sensing & GIS",
      "Transportation Engineering & Management",
      "Multidisciplinary Sustainable Infrastructure",
    ],
  },
  {
    number:    "12",
    color:     "#BF8B2E",
    lightBg:   "bg-[#BF8B2E]/8",
    border:    "border-[#BF8B2E]/20",
    iconColor: "text-[#BF8B2E]",
    numColor:  "text-[#BF8B2E]",
    icon:      RefreshCw,
    title:     "Responsible Consumption & Production",
    tagline:   "SDG 12",
    desc:      "By focusing on recycled materials, sustainable concrete, geosynthetics and geotextiles, the conference champions responsible consumption in civil construction and material production.",
    topics: [
      "Concrete with Recycled Materials",
      "Sustainability & Durability of Concrete",
      "Geosynthetic & Geotextile Applications",
      "Soil Stabilization Materials & Methods",
    ],
  },
];

// ─────────────────────────────────────────────────────────────────
export default function SDGSection() {
  return (
    <section className="w-full bg-white">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-20 lg:py-28">

        {/* ── Section Header ── */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">

          <div className="max-w-2xl">
            <motion.div {...fadeUp(0.05)} className="flex items-center gap-3 mb-5">
              <div className="h-px w-8 bg-[#F58220]" />
              <span className="text-[#F58220] text-[11px] font-bold tracking-[0.22em] uppercase">
                UN Sustainable Development Goals
              </span>
            </motion.div>

            <motion.h2
              {...fadeUp(0.1)}
              className="text-gray-900 text-3xl lg:text-[42px] font-black leading-tight tracking-tight mb-4"
            >
              Aligned with
              <span className="text-[#F58220]"> Global Goals</span>
            </motion.h2>

            <motion.p {...fadeUp(0.16)} className="text-gray-500 text-[15px] leading-relaxed">
              SMPACE-2026 is directly aligned with four United Nations Sustainable Development
              Goals. Every research track and topic presented at the conference contributes
              meaningfully to the global sustainability agenda.
            </motion.p>
          </div>

          {/* Right — SDG logo strip */}
          <motion.div {...fadeRight(0.2)} className="flex-shrink-0">
            <p className="text-gray-400 text-[10px] font-bold tracking-[0.2em] uppercase mb-3 lg:text-right">
              Contributing to
            </p>
            <div className="flex items-center gap-3">
              {SDGS.map((sdg) => (
                <div
                  key={sdg.number}
                  className="flex flex-col items-center justify-center rounded-xl w-[64px] h-[64px] shadow-sm border"
                  style={{
                    backgroundColor: sdg.color,
                    borderColor: sdg.color,
                  }}
                >
                  <span className="text-white text-[9px] font-bold opacity-80 tracking-wider">
                    SDG
                  </span>
                  <span className="text-white text-[24px] font-black leading-none">
                    {sdg.number}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-[11px] mt-3 lg:text-right">
              Sustainable Development Goals · United Nations
            </p>
          </motion.div>
        </div>

        {/* ── SDG Cards ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SDGS.map((sdg, i) => {
            const Icon = sdg.icon;
            return (
              <motion.div
                key={sdg.number}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.52, ease: "easeOut", delay: i * 0.1 }}
                className={`
                  group relative overflow-hidden
                  bg-white border ${sdg.border}
                  rounded-2xl p-7
                  hover:shadow-lg hover:-translate-y-1
                  transition-all duration-300
                `}
              >
                {/* Background number watermark */}
                <span
                  className="absolute -right-4 -bottom-6 text-[120px] font-black leading-none select-none pointer-events-none opacity-[0.04]"
                  style={{ color: sdg.color }}
                >
                  {sdg.number}
                </span>

                {/* Top row — icon + SDG badge */}
                <div className="flex items-start justify-between mb-5">
                  <div
                    className={`w-12 h-12 rounded-xl ${sdg.lightBg} flex items-center justify-center flex-shrink-0`}
                  >
                    <Icon size={22} className={sdg.iconColor} />
                  </div>

                  {/* SDG number badge */}
                  <div
                    className="flex flex-col items-center justify-center rounded-xl w-14 h-14 shadow-sm"
                    style={{ backgroundColor: sdg.color }}
                  >
                    <span className="text-white text-[9px] font-bold opacity-80 tracking-wider">
                      SDG
                    </span>
                    <span className="text-white text-[22px] font-black leading-none">
                      {sdg.number}
                    </span>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-gray-900 text-[20px] font-black leading-tight mb-1">
                  {sdg.title}
                </h3>
                <p className={`${sdg.numColor} text-[11px] font-bold tracking-widest uppercase mb-4`}>
                  {sdg.tagline} · United Nations
                </p>

                {/* Description */}
                <p className="text-gray-500 text-[14px] leading-relaxed mb-6">
                  {sdg.desc}
                </p>

                {/* Divider */}
                <div className={`border-t ${sdg.border} mb-5`} />

                {/* Related research topics */}
                <div>
                  <p className="text-gray-400 text-[10px] font-bold tracking-[0.18em] uppercase mb-3">
                    Related Research Topics
                  </p>
                  <div className="space-y-2">
                    {sdg.topics.map((topic, j) => (
                      <div key={j} className="flex items-center gap-2.5">
                        <span
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: sdg.color }}
                        />
                        <span className="text-gray-600 text-[13px]">{topic}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* ── Bottom Info Banner ── */}
        <motion.div
          {...fadeUp(0.25)}
          className="mt-14 relative overflow-hidden rounded-2xl bg-[#111827] px-8 py-10"
        >
          {/* Decorative bg circles */}
          <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-[#F58220]/5 blur-2xl pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full bg-[#26BDE2]/5 blur-2xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">

            {/* Left text */}
            <div className="max-w-xl">
              <div className="flex items-center gap-3 mb-4">
                <Leaf size={18} className="text-[#F58220]" />
                <span className="text-[#F58220] text-[11px] font-bold tracking-[0.2em] uppercase">
                  Our Commitment
                </span>
              </div>
              <h3 className="text-white text-[24px] font-black leading-snug mb-3">
                Building a Greener &amp; More Resilient
                <span className="text-[#F58220]"> Built Environment</span>
              </h3>
              <p className="text-white/55 text-[14px] leading-relaxed">
                SMPACE-2026 aims to inspire the adoption of sustainable approaches in civil
                engineering, contributing to a greener and more resilient built environment.
                The event offers a platform for sharing research, developing best practices and
                building connections that will shape the future of eco-conscious infrastructure.
              </p>
            </div>

            {/* Right — SDG chips + CTA */}
            <div className="flex flex-col items-start lg:items-end gap-5 flex-shrink-0">
              <div className="flex flex-wrap gap-2">
                {SDGS.map((sdg) => (
                  <div
                    key={sdg.number}
                    className="flex items-center gap-2 border border-white/10 rounded-full px-3 py-1.5"
                  >
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-black flex-shrink-0"
                      style={{ backgroundColor: sdg.color }}
                    >
                      {sdg.number}
                    </span>
                    <span className="text-white/60 text-[11px] font-medium">
                      {sdg.title.split(" ").slice(0, 2).join(" ")}
                    </span>
                  </div>
                ))}
              </div>

              <Link
                to="/research-areas"
                className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#d96d12] text-white text-[13px] font-bold px-6 py-3 rounded-lg transition-all duration-200 group"
              >
                Explore All Research Areas
                <ArrowRight
                  size={15}
                  className="group-hover:translate-x-1 transition-transform duration-200"
                />
              </Link>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}